import React from "react";
import { formElements} from "../CaseFormFields";
import FieldError from "Components/Element/FieldError";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function displayValue(field, formValues) {
  const raw = formValues?.[field.name];

  if (raw === undefined || raw === null || String(raw).trim() === "") return "—";

  // Date fields → format nicely
  if (field.type === "date") {
    const d = new Date(raw);
    if (!isNaN(d))
      return d.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
  }

  // Rating bar → show as "X / 10"
  if (field.type === "ratingBar") return `${raw} / 10`;

  // Select with object options like { name, id } → show name
  if (field.type === "select" && Array.isArray(field.options)) {
    const match = field.options.find(
      (o) => (typeof o === "object" ? o.id === raw : o === raw)
    );
    if (match) return typeof match === "object" ? match.name : match;
  }
  if(field.type === "anonymousCheckbox"){
    return raw ? "Yes" : "No";
  }

  // Multi-select checklists (evidence, day-to-day factors) → item count,
  // matching the design's "3 items provided" summary line.
  if (field.type === "checkboxGroup" && Array.isArray(raw)) {
    if (raw.length === 0) return "—";
    return `${raw.length} item${raw.length === 1 ? "" : "s"} provided`;
  }

  return String(raw);
}

// ─── Skip fields that shouldn't show in review ────────────────────────────────

const SKIP_TYPES = ["textArea"]; // textArea shown separately as a note block

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionBlock({ title, fields, formValues, onEdit, stepIndex }) {
  // reviewHidden marks the granular severity-step follow-ups (e.g. "Were
  // you hospitalized?") that only exist to back the headline answer shown
  // here (e.g. injuriesSeverity) — keeps this screen matching the design's
  // clean per-category summary instead of listing every sub-question.
  // requiredIf (CaseFormFields.js) marks fields that only apply once
  // another field has a specific value - e.g. classActionCommonCause/
  // arbitrationClause only apply once defendant === "yes" (see
  // AdditionalInfoFields.jsx). Reused here so a field that never applied
  // to this case doesn't show up as a stray "—" row.
  // sameLocationCheckbox (e.g. "This happened at the same location I'm
  // located") is a single field in the config, but the client should
  // always see the actual city/state/zipcode as three plain rows here -
  // never a collapsed yes/no line - regardless of whether that box is
  // checked, so its own row is swapped out for its cityField/stateField/
  // zipField sub-fields.
  const expandField = (f) =>
    f.type === "sameLocationCheckbox" ? [f.cityField, f.stateField, f.zipField].filter(Boolean) : [f];

  const shownFields = fields
    .filter((f) => !f.reviewHidden)
    .filter((f) => !f.requiredIf || formValues?.[f.requiredIf.field] === f.requiredIf.value)
    .flatMap(expandField);
  const visibleFields = shownFields.filter((f) => !SKIP_TYPES.includes(f.type));
  const textAreaFields = shownFields.filter((f) => f.type === "textArea");
  const regularFields = visibleFields;

  if (visibleFields.length === 0 && textAreaFields.length === 0) return null;

  return (
    <div className="mb-1 font-plexsans">
      {/* Regular fields table */}
      {regularFields.length > 0 && (
        <div className="rounded-lg border border-[rgba(20,33,61,.12)] overflow-hidden">
          {regularFields.map((field, i) => (
            <div
              key={field.name}
              className={`lg:grid lg:grid-cols-2 lg:gap-6 px-4 py-2.5 text-[13.5px] ${
                i % 2 === 0 ? "bg-ivory" : "bg-[rgba(20,33,61,.02)]"
              } ${i !== regularFields.length - 1 ? "border-b border-[rgba(20,33,61,.08)]" : ""}`}
            >
              <div className="text-[rgba(20,33,61,.5)] mr-4 lg:mr-0 col-span-1 leading-snug w-[100%] lg:max-w-[55%]">
                {field.label}
              </div>
              <div className="font-medium text-navy text-left col-span-1">
                {displayValue(field, formValues)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TextArea fields — shown as a note block */}
      {textAreaFields.map((field) => {
        const val = formValues?.[field.name];
        return (
          <div
            key={field.name}
            className="bg-[rgba(20,33,61,.02)] border border-[rgba(20,33,61,.12)] rounded-lg px-4 py-3 mb-3 mt-2"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[rgba(20,33,61,.4)] mb-1.5">
              {field.label}
            </p>
            <p className="text-[13.5px] text-navy leading-relaxed">
              {val && val.trim() !== "" ? val : <span className="text-[rgba(20,33,61,.4)] italic">No additional notes provided</span>}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function StepReviewBlock({ stepData, formValues, onEdit }) {
  return (
    <div className="mb-6 font-plexsans">
      {/* Step header with Edit button */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-tan">
          {stepLabel(stepData.step)}
        </span>
        <button
          onClick={() => onEdit(stepData.step)}
          className="text-[12px] font-medium text-[rgba(20,33,61,.4)] hover:text-tan transition-colors cursor-pointer bg-transparent border-none p-0"
        >
          Edit
        </button>
      </div>

      {/* Sections */}
      <div className="pl-0">
        {stepData.sections.map((section, i) => (
          <SectionBlock
            key={i}
            title={section.title}
            fields={section.fields}
            formValues={formValues}
            onEdit={onEdit}
            stepIndex={stepData.step - 1}
          />
        ))}
      </div>
      <div className="h-px bg-[rgba(20,33,61,.1)] mt-5" />
    </div>
  );
}

function stepLabel(step) {
  const map = {
    1: "Personal information",
    2: "Case details",
    3: "Additional information",
    4: "Severity",
  };
  return map[step] || `Step ${step}`;
}

// ─── Main Review Component ────────────────────────────────────────────────────

/**
 * Props:
 *  - formValues: object  — your form state object keyed by field `name`
 *  - onEdit: (stepIndex: number) => void  — called when user clicks Edit on a step
 *  - onSubmit: () => void  — called when user confirms and submits
 */
export default function ReviewSection({ formValues = {}, onEdit, onSubmit, isAgreed, setIsAgreed , err, setErr}) {
  return (
    <div className="font-plexsans">
      {/* All steps except the last (step 4 = severity, rendered specially) */}
      {formElements.map((stepData) => (
        <StepReviewBlock
          key={stepData.step}
          stepData={stepData}
          formValues={formValues}
          onEdit={onEdit}
        />
      ))}

      {/* Confirmation checkbox */}
      <ConfirmBlock onSubmit={onSubmit} isAgreed={isAgreed} setIsAgreed={setIsAgreed} err={err} setErr={setErr} />
    </div>
  );
}

// ─── Confirmation Block ───────────────────────────────────────────────────────

function ConfirmBlock({ onSubmit, isAgreed, setIsAgreed, err, setErr }) {

  return (
    <div>
      <div
        className={`flex items-start gap-3 p-3.5 rounded-lg border-[1.5px] transition-colors ${
          err ? "bg-red-50 border-red-300" : "border-[rgba(20,33,61,.15)]"
        }`}
      >
        <input
          id="review-agree"
          type="checkbox"
          checked={isAgreed}
          onChange={(e) => {
            setIsAgreed(e.target.checked);
            if (e.target.checked) setErr(false);
          }}
          className="mt-0.5 w-4 h-4 accent-tan cursor-pointer flex-shrink-0"
        />
        <label
          htmlFor="review-agree"
          className="text-xs lg:text-[12.5px] text-[rgba(20,33,61,.6)] leading-relaxed cursor-pointer"
        >
         I agree to post my case on Quick Case Review so registered attorneys and their assistants can view it and request details if they can help. I also agree to my case information (not my personal details) being processed by AI.
        </label>
      </div>

      {err && <FieldError>You must confirm before submitting.</FieldError>}
    </div>
  );
}
